import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import { 
  Target, 
  Calendar,
  TrendingUp,
  X,
  Save,
  Plus,
  Trash2,
  Edit3,
  Users,
  Activity,
  CheckCircle2
} from 'lucide-react'
import toast from 'react-hot-toast'

interface KPI {
  id: string
  name: string
  description: string | null
  target_value: number
  current_value: number
  unit: string
  frequency: string
}

interface Objective {
  id: number
  title: string
  description: string
  role_type: string
  functional_area: string
  status: string
  progress: number
  start_date: string | null
  end_date: string | null
  is_library_objective: boolean
  kpa: {
    id: number
    name: string
    display_name: string
  } | null
  pillar: {
    id: number
    name: string
    color_code: string
  } | null
  strategic_objective: {
    id: number
    title: string
  } | null
  kpis: KPI[]
  kpi_count: number
  usage_stats?: {
    total_instances: number
    active_instances: number
    unique_users: number
    completion_rate: number
  }
}

interface ObjectiveLibraryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  objective: Objective | null
  mode: 'view' | 'edit'
  onSave: (objectiveId: number, updates: any) => Promise<void>
}

export default function ObjectiveLibraryDialog({
  open,
  onOpenChange,
  objective,
  mode: initialMode,
  onSave
}: ObjectiveLibraryDialogProps) {
  const [mode, setMode] = useState<'view' | 'edit'>(initialMode)
  const [editedObjective, setEditedObjective] = useState<Objective | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (objective) {
      setEditedObjective({ ...objective })
    }
    setMode(initialMode)
  }, [objective, initialMode, open])

  // KPI handlers
  const handleAddKPI = () => {
    const newKPI: KPI = {
      id: `new-${Date.now()}`,
      name: '',
      description: '',
      target_value: 0,
      current_value: 0,
      unit: '%',
      frequency: 'quarterly'
    }
    setEditedObjective(prev => prev ? {
      ...prev,
      kpis: [...(prev.kpis || []), newKPI]
    } : null)
  }

  const handleUpdateKPI = (index: number, field: keyof KPI, value: any) => {
    setEditedObjective(prev => {
      if (!prev) return null
      const kpis = [...(prev.kpis || [])]
      kpis[index] = { ...kpis[index], [field]: value }
      return { ...prev, kpis }
    })
  }

  const handleRemoveKPI = (index: number) => {
    setEditedObjective(prev => {
      if (!prev) return null
      const kpis = [...(prev.kpis || [])]
      kpis.splice(index, 1)
      return { ...prev, kpis }
    })
  }

  const handleSave = async () => {
    if (!editedObjective || !objective) return

    setSaving(true)
    try {
      const updates = {
        title: editedObjective.title,
        description: editedObjective.description,
        start_date: editedObjective.start_date,
        end_date: editedObjective.end_date,
        role_type: editedObjective.role_type,
        functional_area: editedObjective.functional_area,
        status: editedObjective.status,
        kpis: editedObjective.kpis?.map(kpi => ({
          id: kpi.id,
          name: kpi.name,
          description: kpi.description,
          target_value: kpi.target_value,
          current_value: kpi.current_value,
          unit: kpi.unit,
          frequency: kpi.frequency
        }))
      }

      await onSave(objective.id, updates)
      setMode('view')
      toast.success('Objective updated successfully')
    } catch (error) {
      console.error('Error saving objective:', error)
      toast.error('Failed to save objective')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    if (mode === 'edit') {
      setEditedObjective(objective)
      setMode('view')
    } else {
      onOpenChange(false)
    }
  }

  if (!editedObjective) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Library Objective Details</span>
            {mode === 'view' && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setMode('edit')}
              >
                <Edit3 className="h-4 w-4 mr-2" />
                Edit Objective
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-180px)]">
          <div className="space-y-6 pr-4">
            {/* Metadata Badges */}
            <div className="flex flex-wrap gap-2">
              {editedObjective.kpa && (
                <Badge variant="outline">
                  <Target className="h-3 w-3 mr-1" />
                  {editedObjective.kpa.display_name}
                </Badge>
              )}
              {editedObjective.pillar && (
                <Badge 
                  variant="outline" 
                  style={{ 
                    backgroundColor: `${editedObjective.pillar.color_code}20`, 
                    color: editedObjective.pillar.color_code 
                  }}
                >
                  {editedObjective.pillar.name}
                </Badge>
              )}
              {editedObjective.role_type && (
                <Badge variant="outline" className="bg-blue-50">
                  {editedObjective.role_type}
                </Badge>
              )}
              {editedObjective.status && (
                <Badge 
                  variant="outline" 
                  className={
                    editedObjective.status === 'active' ? 'bg-green-50 text-green-700' : 
                    editedObjective.status === 'completed' ? 'bg-slate-50 text-slate-700' : 
                    'bg-amber-50 text-amber-700'
                  }
                >
                  {editedObjective.status}
                </Badge>
              )}
              {editedObjective.kpi_count > 0 && (
                <Badge variant="outline" className="bg-indigo-50 text-indigo-700">
                  <TrendingUp className="h-3 w-3 mr-1" />
                  {editedObjective.kpi_count} KPI{editedObjective.kpi_count > 1 ? 's' : ''}
                </Badge>
              )}
            </div>

            <Separator />

            {/* Objective Details */}
            <div className="space-y-4">
              {/* Title */}
              <div>
                <Label htmlFor="title">Objective Title *</Label>
                {mode === 'view' ? (
                  <p className="text-lg font-semibold text-slate-900 mt-1">{editedObjective.title}</p>
                ) : (
                  <Input
                    id="title"
                    value={editedObjective.title}
                    onChange={(e) => setEditedObjective({ ...editedObjective, title: e.target.value })}
                    placeholder="Enter objective title"
                    className="mt-1"
                  />
                )}
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="description">Description</Label>
                {mode === 'view' ? (
                  <p className="text-sm text-slate-600 mt-1">{editedObjective.description || 'No description provided'}</p>
                ) : (
                  <Textarea
                    id="description"
                    value={editedObjective.description}
                    onChange={(e) => setEditedObjective({ ...editedObjective, description: e.target.value })}
                    placeholder="Describe what you want to achieve and why it matters..."
                    rows={4}
                    className="mt-1"
                  />
                )}
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start_date">
                    <Calendar className="h-4 w-4 inline mr-1" />
                    Start Date
                  </Label>
                  {mode === 'view' ? (
                    <p className="text-sm text-slate-600 mt-1">
                      {editedObjective.start_date 
                        ? new Date(editedObjective.start_date).toLocaleDateString() 
                        : 'Not set'}
                    </p>
                  ) : (
                    <Input
                      id="start_date"
                      type="date"
                      value={editedObjective.start_date ? editedObjective.start_date.split('T')[0] : ''}
                      onChange={(e) => setEditedObjective({ ...editedObjective, start_date: e.target.value })}
                      className="mt-1"
                    />
                  )}
                </div>
                <div>
                  <Label htmlFor="end_date">
                    <Calendar className="h-4 w-4 inline mr-1" />
                    End Date
                  </Label>
                  {mode === 'view' ? (
                    <p className="text-sm text-slate-600 mt-1">
                      {editedObjective.end_date 
                        ? new Date(editedObjective.end_date).toLocaleDateString() 
                        : 'Not set'}
                    </p>
                  ) : (
                    <Input
                      id="end_date"
                      type="date"
                      value={editedObjective.end_date ? editedObjective.end_date.split('T')[0] : ''}
                      onChange={(e) => setEditedObjective({ ...editedObjective, end_date: e.target.value })}
                      className="mt-1"
                    />
                  )}
                </div>
              </div>
            </div>

            <Separator />

            {/* KPIs */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-semibold">
                  <TrendingUp className="h-4 w-4 inline mr-1" />
                  Key impact & growth Indicators ({editedObjective.kpis?.length || 0})
                </Label>
                {mode === 'edit' && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddKPI}
                    className="h-8 text-xs"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add KPI
                  </Button>
                )}
              </div>

              {editedObjective.kpis && editedObjective.kpis.length > 0 ? (
                <div className="space-y-3">
                  {editedObjective.kpis.map((kpi, index) => (
                    <Card key={kpi.id}>
                      <CardContent className="p-4">
                        {mode === 'edit' ? (
                          /* Edit Mode */
                          <div className="space-y-3">
                            <div className="flex gap-2">
                              <div className="flex-1 space-y-2">
                                <Input
                                  placeholder="KPI Name *"
                                  value={kpi.name}
                                  onChange={(e) => handleUpdateKPI(index, 'name', e.target.value)}
                                  className="font-medium"
                                />
                                <Textarea
                                  placeholder="Description (optional)"
                                  value={kpi.description || ''}
                                  onChange={(e) => handleUpdateKPI(index, 'description', e.target.value)}
                                  rows={2}
                                  className="text-sm"
                                />
                                {editedObjective.is_library_objective ? (
                                  /* Library Objective - Only show default target */
                                  <>
                                    <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-3">
                                      <p className="text-xs text-blue-800 font-medium mb-1">📚 Library Template Mode</p>
                                      <p className="text-xs text-blue-700">
                                        Set the default target for this KPI. People Leaders can adjust this when allocating to team members.
                                        Actual values are tracked per team member.
                                      </p>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2">
                                      <div>
                                        <Label className="text-xs text-slate-500">Default Target *</Label>
                                        <Input
                                          type="number"
                                          placeholder="Default target"
                                          value={kpi.target_value || 0}
                                          onChange={(e) => handleUpdateKPI(index, 'target_value', parseFloat(e.target.value) || 0)}
                                          className="mt-1"
                                        />
                                      </div>
                                      <div>
                                        <Label className="text-xs text-slate-500">Unit</Label>
                                        <select
                                          value={kpi.unit || '%'}
                                          onChange={(e) => handleUpdateKPI(index, 'unit', e.target.value)}
                                          className="w-full mt-1 h-10 border border-gray-300 rounded-md px-3 text-sm"
                                        >
                                          <option value="%">%</option>
                                          <option value="#">Number</option>
                                          <option value="hours">Hours</option>
                                          <option value="days">Days</option>
                                          <option value="R">Rands</option>
                                        </select>
                                      </div>
                                      <div>
                                        <Label className="text-xs text-slate-500">Frequency</Label>
                                        <select
                                          value={kpi.frequency || 'quarterly'}
                                          onChange={(e) => handleUpdateKPI(index, 'frequency', e.target.value)}
                                          className="w-full mt-1 h-10 border border-gray-300 rounded-md px-3 text-sm"
                                        >
                                          <option value="bi-annually">Bi-annually (6 months)</option>
                                          <option value="annually">Annually</option>
                                          <option value="quarterly">Quarterly</option>
                                          <option value="monthly">Monthly</option>
                                          <option value="weekly">Weekly</option>
                                          <option value="daily">Daily</option>
                                        </select>
                                      </div>
                                    </div>
                                  </>
                                ) : (
                                  /* Team Member Objective - Show both target and actual */
                                  <>
                                    <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-3">
                                      <p className="text-xs text-green-800 font-medium mb-1">👤 Team Member Objective</p>
                                      <p className="text-xs text-green-700">
                                        Track both target and actual values for this team member's KPI.
                                      </p>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 mb-2">
                                      <div>
                                        <Label className="text-xs text-slate-500">Target Value *</Label>
                                        <Input
                                          type="number"
                                          placeholder="Target"
                                          value={kpi.target_value || 0}
                                          onChange={(e) => handleUpdateKPI(index, 'target_value', parseFloat(e.target.value) || 0)}
                                          className="mt-1"
                                        />
                                      </div>
                                      <div>
                                        <Label className="text-xs text-slate-500">Actual Value</Label>
                                        <Input
                                          type="number"
                                          placeholder="Actual"
                                          value={kpi.current_value || 0}
                                          onChange={(e) => handleUpdateKPI(index, 'current_value', parseFloat(e.target.value) || 0)}
                                          className="mt-1"
                                        />
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                      <div>
                                        <Label className="text-xs text-slate-500">Unit</Label>
                                        <select
                                          value={kpi.unit || '%'}
                                          onChange={(e) => handleUpdateKPI(index, 'unit', e.target.value)}
                                          className="w-full mt-1 h-10 border border-gray-300 rounded-md px-3 text-sm"
                                        >
                                          <option value="%">%</option>
                                          <option value="#">Number</option>
                                          <option value="hours">Hours</option>
                                          <option value="days">Days</option>
                                          <option value="R">Rands</option>
                                        </select>
                                      </div>
                                      <div>
                                        <Label className="text-xs text-slate-500">Frequency</Label>
                                        <select
                                          value={kpi.frequency || 'quarterly'}
                                          onChange={(e) => handleUpdateKPI(index, 'frequency', e.target.value)}
                                          className="w-full mt-1 h-10 border border-gray-300 rounded-md px-3 text-sm"
                                        >
                                          <option value="bi-annually">Bi-annually (6 months)</option>
                                          <option value="annually">Annually</option>
                                          <option value="quarterly">Quarterly</option>
                                          <option value="monthly">Monthly</option>
                                          <option value="weekly">Weekly</option>
                                          <option value="daily">Daily</option>
                                        </select>
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveKPI(index)}
                                className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 mt-1"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          /* View Mode */
                          <div className="space-y-2">
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-medium text-slate-900">{kpi.name}</p>
                                {kpi.description && (
                                  <p className="text-sm text-slate-600 mt-1">{kpi.description}</p>
                                )}
                              </div>
                              {editedObjective.is_library_objective && (
                                <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs shrink-0">
                                  📚 Template
                                </Badge>
                              )}
                            </div>
                            {editedObjective.is_library_objective ? (
                              /* Library Objective - Only show default target */
                              <div className="grid grid-cols-2 gap-4 mt-3">
                                <div>
                                  <p className="text-xs text-slate-500">Default Target</p>
                                  <p className="font-semibold text-lg">{kpi.target_value} {kpi.unit}</p>
                                  <p className="text-xs text-slate-500 mt-1">
                                    Can be adjusted when allocated
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-slate-500">Frequency</p>
                                  <p className="font-semibold capitalize">{kpi.frequency}</p>
                                </div>
                              </div>
                            ) : (
                              /* Team Member Objective - Show target, current, and progress */
                              <>
                                <div className="grid grid-cols-4 gap-4 mt-3">
                                  <div>
                                    <p className="text-xs text-slate-500">Target Value</p>
                                    <p className="font-semibold text-lg">{kpi.target_value} {kpi.unit}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-slate-500">Actual Value</p>
                                    <p className="font-semibold text-lg text-blue-600">{kpi.current_value} {kpi.unit}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-slate-500">Progress</p>
                                    <p className="font-semibold text-lg">
                                      {kpi.target_value > 0 
                                        ? Math.round((kpi.current_value / kpi.target_value) * 100) 
                                        : 0}%
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-slate-500">Frequency</p>
                                    <p className="font-semibold capitalize">{kpi.frequency}</p>
                                  </div>
                                </div>
                                <div className="mt-2">
                                  <div className="w-full bg-slate-200 rounded-full h-2">
                                    <div 
                                      className="bg-blue-600 h-2 rounded-full transition-all"
                                      style={{ 
                                        width: `${Math.min(100, kpi.target_value > 0 ? (kpi.current_value / kpi.target_value) * 100 : 0)}%` 
                                      }}
                                    />
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-slate-50 rounded-lg">
                  <TrendingUp className="h-12 w-12 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-600">
                    {mode === 'edit' ? 'No KPIs yet. Click "Add KPI" to create one.' : 'No KPIs defined for this objective'}
                  </p>
                </div>
              )}
            </div>

            <Separator />

            {/* Usage Statistics */}
            {editedObjective.usage_stats && (
              <div>
                <Label className="text-base font-semibold mb-3 block">Usage Statistics</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Activity className="h-4 w-4 text-blue-600" />
                        <p className="text-xs text-slate-500">Total Uses</p>
                      </div>
                      <p className="text-2xl font-bold text-slate-900">
                        {editedObjective.usage_stats.total_instances}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">instances deployed</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <p className="text-xs text-slate-500">Active</p>
                      </div>
                      <p className="text-2xl font-bold text-green-600">
                        {editedObjective.usage_stats.active_instances}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">active objectives</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Users className="h-4 w-4 text-purple-600" />
                        <p className="text-xs text-slate-500">Users</p>
                      </div>
                      <p className="text-2xl font-bold text-purple-600">
                        {editedObjective.usage_stats.unique_users}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">unique users</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <TrendingUp className="h-4 w-4 text-orange-600" />
                        <p className="text-xs text-slate-500">Active Rate</p>
                      </div>
                      <p className="text-2xl font-bold text-orange-600">
                        {editedObjective.usage_stats.completion_rate}%
                      </p>
                      <p className="text-xs text-slate-500 mt-1">of instances</p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {editedObjective.usage_stats && <Separator />}

            {/* Strategic Alignment */}
            {editedObjective.strategic_objective && (
              <div>
                <Label className="text-base font-semibold">Strategic Alignment</Label>
                <Card className="mt-2">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-blue-600" />
                      <span className="font-medium">{editedObjective.strategic_objective.title}</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            <X className="h-4 w-4 mr-2" />
            {mode === 'edit' ? 'Cancel' : 'Close'}
          </Button>
          {mode === 'edit' && (
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
